import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Loader2,
  Receipt,
  RefreshCw,
  Search,
  XCircle,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { formatDoctorName } from "@/lib/doctorName";

const REVIEW_STATUSES = ["proof_submitted", "pending", "verified", "cancelled", "expired"];

export default function PaymentReview({ navigate }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("proof_submitted");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: e } = await supabase
      .from("payment_links")
      .select(`
        id,
        appointment_id,
        patient_id,
        amount_centavos,
        currency,
        qr_method,
        qr_label,
        status,
        proof_url,
        reference_number,
        proof_submitted_at,
        verified_at,
        expires_at,
        created_at,
        patients (
          id,
          first_name,
          last_name,
          patient_number
        ),
        appointments (
          id,
          scheduled_start,
          status,
          chairs ( name ),
          doctors ( display_name )
        )
      `)
      .in("status", REVIEW_STATUSES)
      .order("proof_submitted_at", { ascending: false, nullsFirst: false });

    setLoading(false);
    if (e) {
      setError(e.message ?? "Could not load payment proofs.");
      return;
    }
    const nextRows = Array.isArray(data) ? data : [];
    setRows(nextRows);
    setSelectedId((current) => current || nextRows[0]?.id || null);
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    proof_submitted: rows.filter((row) => row.status === "proof_submitted").length,
    pending: rows.filter((row) => row.status === "pending").length,
    verified: rows.filter((row) => row.status === "verified").length,
  }), [rows]);

  const filteredRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!needle) return true;
      const haystack = [
        patientName(row),
        row.patients?.patient_number,
        row.qr_label,
        row.qr_method,
        row.reference_number,
        row.appointment_id,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(needle);
    });
  }, [query, rows, statusFilter]);

  const selected = filteredRows.find((row) => row.id === selectedId)
    || filteredRows[0]
    || rows.find((row) => row.id === selectedId)
    || rows[0]
    || null;

  useEffect(() => {
    if (selected?.id && selected.id !== selectedId) setSelectedId(selected.id);
  }, [selected?.id, selectedId]);

  return (
    <div className="mx-auto max-w-5xl space-y-4" data-testid="payment-review">
      <div className="rounded-2xl bg-white px-5 pt-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-6 border-b border-slate-100">
          <button
            type="button"
            onClick={() => navigate?.("/checkout")}
            className="whitespace-nowrap px-1 pb-3 text-lg font-semibold leading-none tracking-normal text-slate-500 transition hover:text-slate-900"
          >
            Checkout queue
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-3 whitespace-nowrap border-b-2 border-teal-600 px-1 pb-3 text-lg font-semibold leading-none tracking-normal text-slate-900"
            aria-current="page"
          >
            <span className="tracking-normal">Payment Review</span>
            <span className="inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 px-2 text-xs font-semibold leading-none tracking-normal text-slate-600">
              {counts.proof_submitted}
            </span>
          </button>
        </div>

        <div className="flex flex-wrap items-start justify-between gap-3 py-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">Payment proofs</p>
            <p className="mt-1 max-w-2xl text-xs text-slate-500">
              {counts.proof_submitted} pending review. Verify receipts before opening checkout.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"
          >
            <RefreshCw className="h-3.5 w-3.5" />Refresh
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 py-3">
          <ReviewFilterButton active={statusFilter === "proof_submitted"} onClick={() => setStatusFilter("proof_submitted")}>
            Pending {counts.proof_submitted}
          </ReviewFilterButton>
          <ReviewFilterButton active={statusFilter === "pending"} onClick={() => setStatusFilter("pending")}>
            Waiting {counts.pending}
          </ReviewFilterButton>
          <ReviewFilterButton active={statusFilter === "verified"} onClick={() => setStatusFilter("verified")}>
            Verified {counts.verified}
          </ReviewFilterButton>
          <ReviewFilterButton active={statusFilter === "all"} onClick={() => setStatusFilter("all")}>
            All
          </ReviewFilterButton>
          <div className="ml-auto flex min-w-64 items-center gap-2 rounded border border-slate-200 bg-white px-3 py-1.5">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search patient, method, ref"
              className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
            />
          </div>
        </div>
      </div>

      {error ? (
        <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="rounded-2xl bg-white p-10 text-center text-sm text-slate-400 shadow-sm">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading payment review...
        </div>
      ) : rows.length === 0 ? (
        <EmptyPaymentReview />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[380px_minmax(0,1fr)]">
          <section className="overflow-hidden rounded-2xl bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3">
              <p className="text-sm font-semibold text-slate-900">Queue</p>
              <p className="text-xs text-slate-500">{filteredRows.length} receipt{filteredRows.length === 1 ? "" : "s"} shown</p>
            </div>
            {filteredRows.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">No receipts match this view.</div>
            ) : (
              <ul className="max-h-[620px] divide-y divide-slate-100 overflow-y-auto">
                {filteredRows.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(row.id)}
                      className={`w-full px-4 py-3 text-left hover:bg-slate-50 ${
                        selected?.id === row.id ? "bg-teal-50/80" : "bg-white"
                      }`}
                    >
                      <PaymentReviewQueueRow row={row} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <PaymentReviewDetail row={selected} navigate={navigate} />
        </div>
      )}
    </div>
  );
}

function ReviewFilterButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
        active
          ? "bg-teal-50 text-teal-800 ring-1 ring-teal-200"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
      }`}
    >
      {children}
    </button>
  );
}

function PaymentReviewQueueRow({ row }) {
  return (
    <div className="flex items-start gap-3">
      <StatusIcon status={row.status} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <p className="truncate text-sm font-semibold text-slate-950">{patientName(row)}</p>
          <p className="shrink-0 text-sm font-semibold tabular-nums text-slate-900">
            {fmtPeso(row.amount_centavos, row.currency)}
          </p>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
          <span>{methodLabel(row)}</span>
          <span className="text-slate-300">·</span>
          <span>{statusLabel(row.status)}</span>
        </div>
        <p className="mt-1 truncate text-xs text-slate-400">
          {row.proof_submitted_at ? `Uploaded ${formatDateTime(row.proof_submitted_at)}` : "Waiting for patient upload"}
        </p>
      </div>
    </div>
  );
}

function PaymentReviewDetail({ row, navigate }) {
  if (!row) {
    return (
      <section className="rounded-2xl bg-white p-10 text-center text-sm text-slate-500 shadow-sm">
        Select a payment proof to review.
      </section>
    );
  }

  return (
    <section className="rounded-2xl bg-white shadow-sm">
      <div className="border-b border-slate-100 px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Selected receipt</p>
            <h2 className="mt-1 text-xl font-semibold text-slate-950">{patientName(row)}</h2>
            <p className="mt-1 text-sm text-slate-500">
              {row.patients?.patient_number || "No patient number"} · {methodLabel(row)}
            </p>
          </div>
          <StatusPill status={row.status} />
        </div>
      </div>

      <PaymentProofUploadedPanel row={row} navigate={navigate} />
    </section>
  );
}

export function PaymentProofUploadedPanel({ row, navigate, compact = false, showCheckoutButton = true }) {
  const appointmentDate = row?.appointments?.scheduled_start
    ? formatDateTime(row.appointments.scheduled_start)
    : "No appointment time";
  const proofMinHeight = compact ? "min-h-44" : "min-h-80";
  const proofMaxHeight = compact ? "max-h-64" : "max-h-[520px]";
  return (
    <div className={`grid gap-5 ${compact ? "p-0" : "p-5 xl:grid-cols-[minmax(0,1fr)_300px]"}`}>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-slate-900">Patient proof</p>
          <p className="text-xs text-slate-500">{methodLabel(row)}</p>
        </div>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
          {row?.proof_url ? (
            <a href={row.proof_url} target="_blank" rel="noopener" className="block bg-white">
              <img
                src={row.proof_url}
                alt="Patient proof"
                data-testid="payment-review-proof-image"
                className={`${proofMaxHeight} w-full object-contain`}
              />
            </a>
          ) : (
            <div className={`flex ${proofMinHeight} flex-col items-center justify-center p-8 text-center text-slate-500`}>
              <Clock className="h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-semibold text-slate-700">No proof uploaded yet</p>
              <p className="mt-1 text-xs">The patient has the payment link, but no screenshot is attached.</p>
            </div>
          )}
        </div>
        {row?.proof_url ? (
          <a
            href={row.proof_url}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 hover:underline"
          >
            Open full receipt <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : null}
      </div>

      <aside className="space-y-3">
        <div className="rounded-xl border border-slate-200 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Claimed amount</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">
            {fmtPeso(row?.amount_centavos, row?.currency)}
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 p-4 text-sm">
          <InfoLine label="Uploaded" value={row?.proof_submitted_at ? formatDateTime(row.proof_submitted_at) : "Not yet"} />
          <InfoLine label="Appointment" value={appointmentDate} />
          <InfoLine label="Doctor" value={formatDoctorName(row?.appointments?.doctors, "Not assigned")} />
          <InfoLine label="Chair" value={row?.appointments?.chairs?.name || "Not assigned"} />
          <InfoLine label="Reference" value={row?.reference_number || "Needs entry in checkout"} />
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-semibold">Verify inside checkout</p>
          <p className="mt-1 text-xs leading-relaxed">
            The receipt is evidence only. Open the appointment, confirm the reference, then complete checkout so payment posting and allocation stay atomic.
          </p>
        </div>

        {showCheckoutButton ? (
          <button
            type="button"
            onClick={() => navigate?.(`/checkout?appointment_id=${row.appointment_id}`)}
            data-testid="payment-review-open-checkout"
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700"
          >
            <Receipt className="h-4 w-4" />Open checkout
          </button>
        ) : null}
      </aside>
    </div>
  );
}

function EmptyPaymentReview() {
  return (
    <div className="rounded-2xl bg-white p-12 text-center shadow-sm">
      <Receipt className="mx-auto h-8 w-8 text-slate-300" />
      <p className="mt-3 text-sm font-semibold text-slate-700">No payment proofs yet.</p>
      <p className="mt-1 text-xs text-slate-500">Uploaded GCash, Maya, and bank-transfer receipts will appear here.</p>
    </div>
  );
}

function StatusIcon({ status }) {
  const cls = "mt-0.5 h-4 w-4 shrink-0";
  if (status === "verified") return <CheckCircle2 className={`${cls} text-emerald-600`} />;
  if (status === "cancelled" || status === "expired") return <XCircle className={`${cls} text-slate-400`} />;
  if (status === "proof_submitted") return <AlertTriangle className={`${cls} text-amber-600`} />;
  return <Clock className={`${cls} text-slate-400`} />;
}

function StatusPill({ status }) {
  const tone = status === "verified"
    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
    : status === "proof_submitted"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-slate-200 bg-slate-50 text-slate-600";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${tone}`}>
      <StatusIcon status={status} />{statusLabel(status)}
    </span>
  );
}

function InfoLine({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-2 last:border-b-0">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-800">{value}</span>
    </div>
  );
}

function patientName(row) {
  const p = row?.patients || {};
  return p.full_name || [p.first_name, p.last_name].filter(Boolean).join(" ") || "(no name)";
}

function methodLabel(row) {
  return row?.qr_label || labelize(row?.qr_method) || "Digital payment";
}

function statusLabel(status) {
  if (status === "proof_submitted") return "Pending review";
  if (status === "pending") return "Waiting upload";
  if (status === "verified") return "Verified";
  if (status === "cancelled") return "Cancelled";
  if (status === "expired") return "Expired";
  return labelize(status);
}

function labelize(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function fmtPeso(cents, currency = "PHP") {
  const value = Number(cents || 0) / 100;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency || "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
